/**
 * Flow editor — Save-As and Load-Library dialogs.
 *
 * Portal-based modals that match the editor's purple/violet theme. Closed
 * by clicking the backdrop, pressing Escape, or hitting Cancel.
 */

import React, { useCallback, useEffect, useRef, useState } from 'react';
import { DialogShell } from './FlowDialogShell';
import { IcFolder, IcRefresh, IcUpload, IcMusic, IcSearch, IcImage } from '../../components/ui/icons';
import MapListRow from '../../components/ui/MapListRow';
import { listServerCharts } from './chartIO';
import {
  isFsAccessSupported,
  listFolderCharts,
  deleteFolderChart,
  type LocalChartEntry,
} from './flowFsAccess';
import { useFlowStore } from './flowStore';
import { loadAudioFile } from './audioLoader';
import { detectAudioOffset } from './audioAutoDetect';
import {
  extractMp3Metadata,
  getImageDimensions,
  parseFilenameMetadata,
} from './audioMetadata';
import { searchCoverArt } from './coverSearch';
import { color } from '../../components/ui/theme';
import {
  codeStyle,
  labelStyle,
  inputStyle,
  hintStyle,
  primaryBtnStyle,
  secondaryBtnStyle,
  cancelBtnStyle,
  listWrapStyle,
  listStyle,
  loadingStyle,
  emptyStyle,
  errorStyle,
} from './FlowDialogs.styles';

/** Multi-keyword search: every whitespace-separated token must appear somewhere
 *  in the text (substring, anywhere — not just the first word / a prefix). */
function matchesQuery(text: string, query: string): boolean {
  const q = query.trim().toLowerCase();
  if (!q) return true;
  const hay = text.toLowerCase();
  return q.split(/\s+/).every(tok => hay.includes(tok));
}

// ── SaveAs dialog ───────────────────────────────────────────────────────────

interface SaveAsDialogProps {
  initialName: string;
  onCancel: () => void;
  onConfirm: (name: string) => void;
  /** Where the map will be written (e.g. a local folder). Defaults to the
   *  server library when omitted. */
  targetLabel?: string;
}

/** Mirror the backend's _slugify_chart_name so we can detect name collisions
 *  client-side before saving (prevents silently overwriting another map). */
function slugifyChartName(name: string): string {
  let cleaned = name.replace(/[^\w\-. ]+/g, '_').trim();
  cleaned = cleaned.replace(/\s+/g, '_');
  if (!cleaned) cleaned = 'untitled';
  if (!cleaned.toLowerCase().endsWith('.flow')) cleaned = `${cleaned}.flow`;
  return cleaned;
}

export function SaveAsDialog({ initialName, onCancel, onConfirm, targetLabel }: SaveAsDialogProps) {
  const [name, setName] = useState(initialName);
  const [existing, setExisting] = useState<string[]>([]);
  const [confirmingOverwrite, setConfirmingOverwrite] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    inputRef.current?.focus();
    inputRef.current?.select();
  }, []);

  // Pull the existing chart filenames so we can warn before clobbering one.
  // In local-folder mode (targetLabel set) check the folder; otherwise the
  // server library.
  useEffect(() => {
    let alive = true;
    const source = targetLabel
      ? listFolderCharts().then(list => list.map(c => c.name))
      : listServerCharts().then(list => list.map(c => c.name));
    source
      .then(names => { if (alive) setExisting(names.map(n => n.toLowerCase())); })
      .catch(() => { /* offline / no permission — skip the collision check */ });
    return () => { alive = false; };
  }, [targetLabel]);

  // Does the typed name collide with a chart already on disk?
  const collides = existing.includes(slugifyChartName(name).toLowerCase());

  // Reset the "are you sure" arm whenever the name changes.
  useEffect(() => { setConfirmingOverwrite(false); }, [name]);

  const confirm = useCallback(() => {
    const trimmed = name.trim();
    if (!trimmed) return;
    // Two-step guard: first click on a colliding name arms the overwrite,
    // second click actually saves. Non-colliding names save immediately.
    if (collides && !confirmingOverwrite) {
      setConfirmingOverwrite(true);
      return;
    }
    onConfirm(trimmed);
  }, [name, collides, confirmingOverwrite, onConfirm]);

  return (
    <DialogShell
      title="Save beatmap"
      subtitle={targetLabel
        ? <>Saved to {targetLabel}</>
        : <>Stored in <code style={codeStyle}>beatmaps/flow/</code></>}
      onClose={onCancel}
      width={380}
      footer={
        <button
          onClick={confirm}
          style={{
            ...primaryBtnStyle,
            ...(collides ? { background: 'rgba(239, 68, 68, 0.85)', boxShadow: 'none' } : {}),
          }}
          disabled={!name.trim()}
        >
          {collides ? (confirmingOverwrite ? 'Click again to overwrite' : 'Overwrite…') : 'Save'}
        </button>
      }
    >
      <label style={labelStyle}>Name</label>
      <input
        ref={inputRef}
        value={name}
        onChange={e => setName(e.target.value)}
        onKeyDown={e => { if (e.key === 'Enter') confirm(); }}
        placeholder="My beatmap"
        style={{
          ...inputStyle,
          borderColor: collides ? 'rgba(248, 113, 113, 0.6)' : (inputStyle.borderColor as string),
        }}
      />
      {collides ? (
        <div style={{ ...hintStyle, color: '#F87171' }}>
          ⚠ A map named “{slugifyChartName(name).replace(/\.flow$/i, '')}” already
          exists. Saving will <strong>overwrite</strong> it. Pick a different name
          to keep both.
        </div>
      ) : (
        <div style={hintStyle}>
          .flow extension is added automatically.
        </div>
      )}
    </DialogShell>
  );
}

// ── Load Library dialog ─────────────────────────────────────────────────────

interface LoadLibraryDialogProps {
  onClose: () => void;
  onUploadClick: () => void;
  /** Name of the connected local folder (File System Access API), or null. */
  localFolderName?: string | null;
  /** Prompt the user to pick a local folder. */
  onConnectFolder?: () => void;
  /** Open a map straight from the connected local folder. */
  onPickLocal?: (name: string) => void;
}

// Local .flow files only carry name/date/size, so rows render in simple mode.
const stripFlow = (n: string) => n.replace(/\.flow$/i, '');

const searchWrapStyle: React.CSSProperties = { position: 'relative', display: 'flex', alignItems: 'center' };
const searchIconStyle: React.CSSProperties = {
  position: 'absolute', left: 10, top: '50%', transform: 'translateY(-50%)',
  color: color.inkFaint, display: 'flex', pointerEvents: 'none',
};
const searchInputStyle: React.CSSProperties = {
  width: '100%', padding: '8px 10px 8px 32px', borderRadius: 8,
  background: 'oklch(0.18 calc(0.026 * var(--tint-c)) var(--tint-h) / 0.5)',
  border: '1px solid oklch(0.62 calc(0.11 * var(--tint-c)) var(--tint-h) / 0.25)',
  color: 'var(--tx-hi)', fontSize: 12, fontFamily: 'inherit', outline: 'none', boxSizing: 'border-box',
};

export function LoadLibraryDialog({
  onClose, onUploadClick,
  localFolderName, onConnectFolder, onPickLocal,
}: LoadLibraryDialogProps) {
  // Local-folder maps (File System Access API) — the only map source here now.
  const fsSupported = isFsAccessSupported();
  const [localCharts, setLocalCharts] = useState<LocalChartEntry[] | null>(null);
  const [localError, setLocalError] = useState<string | null>(null);
  const [query, setQuery] = useState('');

  const refreshLocal = useCallback(async () => {
    if (!localFolderName) { setLocalCharts(null); return; }
    setLocalError(null);
    try {
      setLocalCharts(await listFolderCharts());
    } catch (e: any) {
      setLocalError(e?.message || 'Could not read the folder');
      setLocalCharts([]);
    }
  }, [localFolderName]);

  useEffect(() => { void refreshLocal(); }, [refreshLocal]);

  const handleDeleteLocal = useCallback(async (name: string) => {
    try {
      await deleteFolderChart(name);
      await refreshLocal();
    } catch (e: any) {
      setLocalError(e?.message || 'Delete failed');
    }
  }, [refreshLocal]);

  return (
    <DialogShell
      title="Open Beatmap"
      subtitle="Open a map from your PC, or upload a .flow file."
      onClose={onClose}
      width={520}
      footer={
        <>
          {fsSupported && (
            <button
              onClick={() => onConnectFolder?.()}
              style={footerBtnStyle}
              title="Pick a folder on your PC to read and save maps directly"
            >
              {localFolderName
                ? <><IcRefresh size={14} /> Change folder</>
                : <><IcFolder size={14} /> Connect a folder</>}
            </button>
          )}
          <button onClick={onUploadClick} style={footerBtnStyle} title="Import a .flow file">
            <IcUpload size={14} /> Upload…
          </button>
        </>
      }
    >
      {/* Your PC — maps in the connected local folder; otherwise a short hint. */}
      {fsSupported && localFolderName ? (
        <section style={sectionStyle}>
          <span style={sectionTitleStyle}>Your PC</span>
          <div style={folderChipStyle} title={localFolderName}>
            <IcFolder size={15} />
            <span style={folderChipNameStyle}>{localFolderName}</span>
          </div>
          {/* Search — matches any keyword, anywhere in the name. */}
          <div style={searchWrapStyle}>
            <span style={searchIconStyle}><IcSearch size={14} /></span>
            <input
              value={query}
              onChange={e => setQuery(e.target.value)}
              placeholder="Search maps…"
              style={searchInputStyle}
              spellCheck={false}
            />
          </div>
          <div style={listWrapStyle}>
            {localCharts === null && <div style={loadingStyle}>Loading…</div>}
            {localCharts && localCharts.length === 0 && !localError && (
              <div style={emptyStyle}>No .flow maps in this folder yet.</div>
            )}
            {localError && <div style={errorStyle}>{localError}</div>}
            {localCharts && localCharts.length > 0 && (() => {
              const filtered = localCharts.filter(c => matchesQuery(c.name.replace(/\.flow$/, ''), query));
              if (filtered.length === 0) return <div style={emptyStyle}>No maps match “{query}”.</div>;
              return (
                <ul style={listStyle}>
                  {filtered.map(c => (
                    <MapListRow
                      key={c.name}
                      name={c.name}
                      meta={`${fmtDate(c.modified)} · ${fmtSize(c.size)}`}
                      cleanName={stripFlow}
                      onPick={() => onPickLocal?.(c.name)}
                      onDelete={() => handleDeleteLocal(c.name)}
                    />
                  ))}
                </ul>
              );
            })()}
          </div>
        </section>
      ) : (
        <div style={emptyBodyStyle}>
          {fsSupported
            ? 'Connect a folder on your PC — or upload a .flow file — to load a map.'
            : 'Upload a .flow file to load a map.'}
        </div>
      )}
    </DialogShell>
  );
}

// A labelled block (Your PC) with its own title + content.
const sectionStyle: React.CSSProperties = {
  display: 'flex',
  flexDirection: 'column',
  gap: 6,
};

// Centered hint shown when there's no connected folder to list.
const emptyBodyStyle: React.CSSProperties = {
  padding: '34px 16px',
  textAlign: 'center',
  color: '#7A7099',
  fontSize: 12,
  lineHeight: 1.5,
  border: '1px solid oklch(0.62 calc(0.11 * var(--tint-c)) var(--tint-h) / 0.18)',
  borderRadius: 8,
  background: 'rgba(8, 5, 20, 0.6)',
};

const sectionTitleStyle: React.CSSProperties = {
  fontSize: 10.5,
  fontWeight: 700,
  letterSpacing: 1,
  textTransform: 'uppercase',
  color: color.inkDim,
};

// Connected-folder chip showing the picked folder's name.
const folderChipStyle: React.CSSProperties = {
  display: 'flex',
  alignItems: 'center',
  gap: 7,
  padding: '7px 11px',
  borderRadius: 8,
  background: 'oklch(0.55 calc(0.16 * var(--accent-c)) var(--accent-h) / 0.14)',
  border: '1px solid oklch(0.70 calc(0.205 * var(--accent-c)) var(--accent-h) / 0.3)',
  color: '#F5E8FF',
  fontSize: 12,
  fontWeight: 600,
};
const folderChipNameStyle: React.CSSProperties = {
  overflow: 'hidden',
  textOverflow: 'ellipsis',
  whiteSpace: 'nowrap',
};

// Source/import action buttons in the footer row — equal-width, centered.
const footerBtnStyle: React.CSSProperties = {
  flex: 1,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  gap: 6,
  padding: '9px 12px',
  background: 'oklch(0.62 calc(0.11 * var(--tint-c)) var(--tint-h) / 0.10)',
  border: '1px solid oklch(0.62 calc(0.11 * var(--tint-c)) var(--tint-h) / 0.35)',
  borderRadius: 6,
  color: color.ink,
  fontSize: 11.5,
  fontWeight: 600,
  letterSpacing: 0.4,
  cursor: 'pointer',
  fontFamily: 'inherit',
  whiteSpace: 'nowrap',
};

// ── New Map dialog ──────────────────────────────────────────────────────────
//
// One-stop creation flow for a fresh beatmap. Asks the user for:
//   - Song name
//   - Author
//   - Audio file (required — BPM / offset / duration are detected on the fly)
//   - Cover image (optional, packed into the .flow zip on save)
//
// On Create: wipes the current chart (notes + selection), runs the audio
// through /api/audio/analyze, then applies all the metadata at once so the
// editor opens fresh with everything wired up.

interface NewMapDialogProps {
  onClose: () => void;
  /** Fired after a fresh map is successfully created. Receives the project name
   *  the user typed (if any) so the host can seed the "save as" filename —
   *  otherwise it's reset and the next Save prompts for a fresh name. */
  onCreated?: (projectName?: string) => void;
}

interface PendingCover {
  bytes: ArrayBuffer;
  name: string;
  mime: string;
  url: string;
}

export function NewMapDialog({ onClose, onCreated }: NewMapDialogProps) {
  const [projectName, setProjectName] = useState('');
  const [songName, setSongName] = useState('');
  const [songAuthor, setSongAuthor] = useState('');
  // Optional manual BPM — when set, it overrides the analyzer's auto-detection
  // (user feedback: let mappers set the BPM themselves at map creation).
  const [manualBpm, setManualBpm] = useState('');
  const [audioFile, setAudioFile] = useState<File | null>(null);
  const [coverFile, setCoverFile] = useState<PendingCover | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const audioInputRef = useRef<HTMLInputElement>(null);
  const coverInputRef = useRef<HTMLInputElement>(null);

  // Free the cover preview URL on unmount / replace so we don't leak blobs.
  useEffect(() => {
    return () => {
      if (coverFile?.url) {
        try { URL.revokeObjectURL(coverFile.url); } catch { /* ignore */ }
      }
    };
  }, [coverFile]);

  const [autoStatus, setAutoStatus] = useState<string | null>(null);
  /** True while any async metadata / cover lookup is in flight — drives
   *  the loader spinner inside the status pill. */
  const [autoBusy, setAutoBusy] = useState(false);

  /**
   * When the user picks an MP3, peek at its ID3v2 tags so we can pre-fill
   * song / author and pre-load an embedded cover.
   *
   * Fallback chain for each text field (first non-empty wins):
   *   1. What the user already typed.
   *   2. ID3 tag (TIT2 / TPE1).
   *   3. Parsed filename ("Artist - Title.mp3" → artist + title).
   *
   * If we end up with at least one of artist/title AND no >=1024 cover
   * was embedded, automatically reach out to MusicBrainz to grab one —
   * that's the whole point of the auto-flow, no need to make the user
   * click an extra button.
   */
  const pickAudio = useCallback(async (f: File | null | undefined) => {
    if (!f) return;
    setAudioFile(f);
    setError(null);
    setAutoStatus('Reading audio metadata…');
    setAutoBusy(true);

    try {
      const meta = await extractMp3Metadata(f);
      const fromName = parseFilenameMetadata(f.name);

      // Resolve final field values without depending on async setState.
      const currentTitle = songName.trim();
      const currentAuthor = songAuthor.trim();
      const finalTitle = currentTitle
        || (meta.title?.trim() ?? '')
        || (fromName.title?.trim() ?? '');
      const finalAuthor = currentAuthor
        || (meta.artist?.trim() ?? '')
        || (fromName.artist?.trim() ?? '');

      const filledFrom: string[] = [];
      if (!currentTitle && finalTitle) {
        setSongName(finalTitle);
        filledFrom.push(meta.title ? `title (ID3)` : `title (filename)`);
      }
      if (!currentAuthor && finalAuthor) {
        setSongAuthor(finalAuthor);
        filledFrom.push(meta.artist ? `artist (ID3)` : `artist (filename)`);
      }

      // Embedded picture — only adopt it when it meets the 1024 floor and
      // the user hasn't already chosen a cover manually.
      let usedEmbeddedCover = false;
      let embeddedTooSmall = false;
      if (meta.pictureBytes && meta.pictureMime && !coverFile) {
        const blob = new Blob([meta.pictureBytes], { type: meta.pictureMime });
        try {
          const dim = await getImageDimensions(blob);
          if (dim.width >= 1024 && dim.height >= 1024) {
            setCoverFile({
              bytes: meta.pictureBytes,
              name: `cover.${(meta.pictureMime.split('/').pop() || 'jpg').split('+')[0]}`,
              mime: meta.pictureMime,
              url: URL.createObjectURL(blob),
            });
            filledFrom.push(`cover ${dim.width}×${dim.height} (ID3)`);
            usedEmbeddedCover = true;
          } else {
            embeddedTooSmall = true;
          }
        } catch { /* unreadable image — skip */ }
      }

      // If the embedded cover wasn't usable, automatically search the web
      // using whatever artist + title we managed to assemble.
      const searchable = (finalAuthor || finalTitle).trim();
      if (!coverFile && !usedEmbeddedCover && searchable) {
        setAutoStatus(`Auto-filled ${filledFrom.join(' · ') || 'nothing'}${
          embeddedTooSmall ? ' · embedded cover too small' : ''
        } — searching the web for a ≥ 1024×1024 cover…`);
        try {
          const found = await searchCoverArt(finalAuthor, finalTitle, 1024);
          if (found) {
            const blob = new Blob([found.bytes], { type: found.mime });
            const url = URL.createObjectURL(blob);
            setCoverFile({
              bytes: found.bytes,
              name: found.name,
              mime: found.mime,
              url,
            });
            try {
              const dim = await getImageDimensions(blob);
              setAutoStatus(
                `Filled: ${filledFrom.join(' · ') || '—'}; cover ${dim.width}×${dim.height} (MusicBrainz)`,
              );
            } catch {
              setAutoStatus(
                `Filled: ${filledFrom.join(' · ') || '—'}; cover from MusicBrainz`,
              );
            }
          } else {
            setAutoStatus(filledFrom.length > 0
              ? `Auto-filled ${filledFrom.join(' · ')} — no cover found online.`
              : 'No tags or web match — fill the fields manually then hit Auto-fetch.');
          }
        } catch {
          setAutoStatus(filledFrom.length > 0
            ? `Auto-filled ${filledFrom.join(' · ')} — online cover lookup failed.`
            : 'Online cover lookup failed.');
        }
      } else {
        setAutoStatus(filledFrom.length > 0
          ? `Auto-filled: ${filledFrom.join(' · ')}`
          : 'No metadata found in this file.');
      }
    } catch {
      setAutoStatus('Could not read tags from this file.');
    } finally {
      setAutoBusy(false);
    }
  }, [songName, songAuthor, coverFile]);

  /**
   * Search MusicBrainz + Cover Art Archive for a high-res cover based on
   * the current song name / author. Replaces the existing cover when it
   * finds something better. Used both as a manual button and as a
   * fallback when the embedded picture was too small.
   */
  const autoFetchOnline = useCallback(async () => {
    const artist = songAuthor.trim();
    const title = songName.trim();
    if (!artist && !title) {
      setError('Type at least an artist or song name to search the web.');
      return;
    }
    setError(null);
    setAutoStatus('Searching the web for a cover ≥ 1024×1024…');
    setAutoBusy(true);
    try {
      const found = await searchCoverArt(artist, title, 1024);
      if (!found) {
        setAutoStatus('Nothing matched online — leaving the existing cover untouched.');
        return;
      }
      const blob = new Blob([found.bytes], { type: found.mime });
      // Drop any preview URL we already minted for the old cover.
      if (coverFile?.url) {
        try { URL.revokeObjectURL(coverFile.url); } catch { /* ignore */ }
      }
      setCoverFile({
        bytes: found.bytes,
        name: found.name,
        mime: found.mime,
        url: URL.createObjectURL(blob),
      });
      try {
        const dim = await getImageDimensions(blob);
        setAutoStatus(`Fetched cover ${dim.width}×${dim.height} from MusicBrainz`);
      } catch {
        setAutoStatus('Fetched cover from MusicBrainz');
      }
    } catch (e: any) {
      setAutoStatus(null);
      setError(e?.message || 'Online cover lookup failed.');
    } finally {
      setAutoBusy(false);
    }
  }, [songName, songAuthor, coverFile]);

  const pickCover = useCallback(async (f: File | null | undefined) => {
    if (!f) return;
    try {
      const bytes = await f.arrayBuffer();
      if (coverFile?.url) {
        try { URL.revokeObjectURL(coverFile.url); } catch { /* ignore */ }
      }
      setCoverFile({
        bytes,
        name: f.name,
        mime: f.type || 'image/png',
        url: URL.createObjectURL(f),
      });
      setError(null);
    } catch (e: any) {
      setError(e?.message || 'Could not read cover image');
    }
  }, [coverFile]);

  const onCreate = useCallback(async () => {
    if (!audioFile) {
      setError('Pick an audio file to continue — BPM and offset are detected from it.');
      return;
    }
    setBusy(true);
    setError(null);
    try {
      const store = useFlowStore.getState();
      // Reset the editor state for a fresh chart. Notes, selection,
      // cut marker and any in-progress trail get wiped.
      store.clearChart();
      store.clearSelection();
      store.clearCut();
      if (store.pendingTrailNoteId) store.cancelTrail();
      // Drop any previously-loaded cover so it doesn't bleed into the
      // new bundle if the user skipped picking one here.
      store.clearCover();

      // Audio first — sets BPM, offset, duration via the backend analyzer.
      await loadAudioFile(audioFile);

      // Refine the offset client-side (skip the leading silence / white noise),
      // same as the Auto Offset button. Keep the backend offset if it fails.
      try {
        const off = await detectAudioOffset();
        const c = useFlowStore.getState().chart;
        store.setChart({ ...c, offset_seconds: Math.round(off * 1000) / 1000 });
      } catch { /* keep the analyzer's offset */ }

      // Manual BPM wins over the analyzer's auto-detection when provided.
      const typedBpm = parseFloat(manualBpm);
      if (isFinite(typedBpm) && typedBpm >= 40 && typedBpm <= 500) {
        const c = useFlowStore.getState().chart;
        store.setChart({ ...c, bpm: Math.round(typedBpm * 100) / 100 });
      }

      // Apply the rest of the metadata now that the chart's settled.
      store.setSongName(songName.trim());
      store.setSongAuthor(songAuthor.trim());
      if (coverFile) {
        store.setCover({
          url: coverFile.url,
          name: coverFile.name,
          mime: coverFile.mime,
          bytes: coverFile.bytes,
        });
      }
      // Brand-new chart → hand the host the project name so it seeds the
      // "save as" filename (empty → next Save prompts for a fresh name).
      onCreated?.(projectName);
      onClose();
    } catch (e: any) {
      setError(e?.message || 'Could not create new map');
      setBusy(false);
    }
  }, [audioFile, projectName, songName, songAuthor, coverFile, manualBpm, onClose, onCreated]);

  return (
    <DialogShell
      title="New map"
      subtitle="Set the basics for your beatmap. You can edit them later by saving and loading the chart."
      onClose={onClose}
      width={420}
      busy={busy}
      footer={
        <button type="button" style={primaryBtnStyle} onClick={onCreate} disabled={busy}>
          {busy ? 'Creating…' : 'Create'}
        </button>
      }
    >
        <label style={labelStyle}>PROJECT NAME</label>
        <input
          type="text"
          value={projectName}
          placeholder="e.g. My First Map"
          onChange={(e) => setProjectName(e.target.value)}
          style={inputStyle}
          disabled={busy}
        />
        <div style={{ ...hintStyle, marginTop: 4 }}>
          Used as the file name when you save. Leave blank to name it later.
        </div>

        <label style={labelStyle}>AUDIO (required)</label>
        <input
          ref={audioInputRef}
          type="file"
          accept="audio/*,.mp3,.ogg,.wav,.m4a,.flac"
          onChange={(e) => pickAudio(e.target.files?.[0])}
          style={{ display: 'none' }}
        />
        <button
          type="button"
          style={secondaryBtnStyle}
          onClick={() => audioInputRef.current?.click()}
          disabled={busy}
        >
          <IcMusic size={14} /> {audioFile ? audioFile.name : 'Pick audio file'}
        </button>

        <label style={labelStyle}>BPM (optional)</label>
        <input
          type="number"
          min={40}
          max={500}
          step="any"
          value={manualBpm}
          placeholder="Auto-detect from audio"
          onChange={(e) => setManualBpm(e.target.value)}
          style={inputStyle}
          disabled={busy}
        />
        <div style={{ ...hintStyle, marginTop: 4 }}>
          Leave blank to auto-detect (rounded to a whole BPM). Type a value to set it yourself.
        </div>

        <label style={labelStyle}>SONG NAME</label>
        <input
          type="text"
          value={songName}
          placeholder="e.g. MORE"
          onChange={(e) => setSongName(e.target.value)}
          style={inputStyle}
          disabled={busy}
        />

        <label style={labelStyle}>AUTHOR</label>
        <input
          type="text"
          value={songAuthor}
          placeholder="e.g. K/DA"
          onChange={(e) => setSongAuthor(e.target.value)}
          style={inputStyle}
          disabled={busy}
        />

        {/* Auto-fetch lives here so it's right next to the inputs that
            drive the search (song name + author). Clicking searches
            MusicBrainz for a cover ≥ 1024×1024. */}
        <button
          type="button"
          style={{
            ...secondaryBtnStyle,
            marginTop: 4,
            opacity: (!songName.trim() && !songAuthor.trim()) ? 0.55 : 1,
          }}
          onClick={autoFetchOnline}
          disabled={busy || autoBusy}
          title={(!songName.trim() && !songAuthor.trim())
            ? 'Type at least a song name or author first'
            : 'Search MusicBrainz for a cover ≥ 1024×1024'}
        >
          <IcSearch size={14} /> Auto-fetch info &amp; cover from web
        </button>

        <label style={labelStyle}>COVER</label>
        <input
          ref={coverInputRef}
          type="file"
          accept="image/png,image/jpeg,image/webp,.png,.jpg,.jpeg,.webp"
          onChange={(e) => pickCover(e.target.files?.[0])}
          style={{ display: 'none' }}
        />
        <div style={coverRowStyle}>
          <div
            style={{
              ...coverThumbStyle,
              backgroundImage: coverFile ? `url(${coverFile.url})` : undefined,
              backgroundColor: coverFile ? 'transparent' : 'rgba(15, 15, 30, 0.5)',
            }}
            onClick={() => !busy && coverInputRef.current?.click()}
          >
            {!coverFile && <span style={coverPlaceholderStyle}><IcImage size={26} /></span>}
          </div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 4, flex: 1 }}>
            <button
              type="button"
              style={secondaryBtnStyle}
              onClick={() => coverInputRef.current?.click()}
              disabled={busy}
            >
              {coverFile ? 'Replace cover' : 'Pick cover (optional)'}
            </button>
            {coverFile && (
              <button
                type="button"
                style={cancelBtnStyle}
                onClick={() => {
                  if (coverFile.url) {
                    try { URL.revokeObjectURL(coverFile.url); } catch { /* ignore */ }
                  }
                  setCoverFile(null);
                }}
                disabled={busy}
              >
                Remove
              </button>
            )}
          </div>
        </div>

        {autoStatus && (
          <div style={autoStatusStyle}>
            {autoBusy && <LoaderSpinner />}
            <span>{autoStatus}</span>
          </div>
        )}
        {error && (
          <div style={inlineErrorStyle}>{error}</div>
        )}

        {/* Second-stage overlay: shown while the audio is analyzed. */}
        {busy && (
          <div style={calcOverlayStyle}>
            <LoaderSpinner />
            <div style={{ fontSize: 16, fontWeight: 800, color: '#E9D5FF', letterSpacing: 0.4 }}>
              Calculating BPM and offset…
            </div>
            <div style={{ fontSize: 11, color: color.inkDim, opacity: 0.85 }}>
              Analyzing the audio
            </div>
          </div>
        )}
    </DialogShell>
  );
}

const calcOverlayStyle: React.CSSProperties = {
  position: 'absolute',
  inset: 0,
  borderRadius: 12,
  background: 'rgba(8, 6, 20, 0.94)',
  backdropFilter: 'blur(3px)',
  WebkitBackdropFilter: 'blur(3px)',
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  justifyContent: 'center',
  gap: 12,
  zIndex: 5,
};

// ── Edit map info dialog ───────────────────────────────────────────────────
//
// Triggered by clicking the song info banner at the top of the canvas.
// Lets the user fix song name, author and cover without going through the
// "New map" flow (which would wipe notes). The Save button updates the
// store immediately AND calls the parent's save-to-server callback so the
// edited metadata makes it to disk in one shot.

interface EditMapInfoDialogProps {
  onClose: () => void;
  /** Called when the user clicks Save. Receives the edited fields so the
   *  caller can apply them and trigger a server save. The dialog won't have
   *  closed yet — the parent closes it after a successful save. */
  onSave: (next: {
    songName: string;
    songAuthor: string;
    cover: { bytes: ArrayBuffer; name: string; mime: string; url: string } | null;
    removeCover: boolean;
  }) => Promise<void> | void;
  /** Open the audio file picker to replace the track. */
  onReplaceAudio?: () => void;
  /** True while a newly picked audio file is decoding. */
  audioLoading?: boolean;
}

export function EditMapInfoDialog({ onClose, onSave, onReplaceAudio, audioLoading }: EditMapInfoDialogProps) {
  const initialSongName = useFlowStore(s => s.songName);
  const initialSongAuthor = useFlowStore(s => s.songAuthor);
  const initialCoverUrl = useFlowStore(s => s.coverUrl);
  const audioName = useFlowStore(s => s.audioName);

  // If the store fields are empty (typical when the user dropped an MP3 via
  // the sidebar without going through the New Map flow), fall back to the
  // same derivation the banner uses so the dialog isn't blank.
  const filenameMeta = audioName ? parseFilenameMetadata(audioName) : { title: '', artist: '' };
  const derivedName = initialSongName.trim()
    || filenameMeta.title?.trim()
    || (audioName ? audioName.replace(/\.[^.]+$/, '') : '');
  const derivedAuthor = initialSongAuthor.trim() || filenameMeta.artist?.trim() || '';

  const [songName, setSongName] = useState(derivedName);
  const [songAuthor, setSongAuthor] = useState(derivedAuthor);
  // null = no change (keep whatever's in the store). When the user picks a
  // new cover we hold it here until Save. When they click Remove we flip
  // `removeCover` so the parent knows to clear, even with `pendingCover=null`.
  const [pendingCover, setPendingCover] = useState<PendingCover | null>(null);
  const [removeCover, setRemoveCover] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [autoStatus, setAutoStatus] = useState<string | null>(null);
  const [autoBusy, setAutoBusy] = useState(false);

  const coverInputRef = useRef<HTMLInputElement>(null);

  // Decide which cover preview to render: a pending pick, the store's
  // current cover (if not flagged for removal), or nothing.
  const previewUrl = pendingCover?.url
    ?? (removeCover ? null : initialCoverUrl);

  // Free preview URL on unmount / replace.
  useEffect(() => {
    return () => {
      if (pendingCover?.url) {
        try { URL.revokeObjectURL(pendingCover.url); } catch { /* ignore */ }
      }
    };
  }, [pendingCover]);

  const pickCover = useCallback(async (f: File | null | undefined) => {
    if (!f) return;
    try {
      const bytes = await f.arrayBuffer();
      if (pendingCover?.url) {
        try { URL.revokeObjectURL(pendingCover.url); } catch { /* ignore */ }
      }
      setPendingCover({
        bytes,
        name: f.name,
        mime: f.type || 'image/png',
        url: URL.createObjectURL(f),
      });
      setRemoveCover(false);
      setError(null);
    } catch (e: any) {
      setError(e?.message || 'Could not read cover image');
    }
  }, [pendingCover]);

  const handleRemoveCover = useCallback(() => {
    if (pendingCover?.url) {
      try { URL.revokeObjectURL(pendingCover.url); } catch { /* ignore */ }
    }
    setPendingCover(null);
    setRemoveCover(true);
  }, [pendingCover]);

  /** Mirror of the NewMapDialog auto-fetch: hit MusicBrainz / Cover Art
   *  Archive using the current song name + author, replace the preview with
   *  whatever we get back. Useful when an existing chart was saved without
   *  a cover and the user wants to backfill it. */
  const autoFetchOnline = useCallback(async () => {
    const artist = songAuthor.trim();
    const title = songName.trim();
    if (!artist && !title) {
      setError('Type at least a song name or author to search.');
      return;
    }
    setError(null);
    setAutoStatus('Searching the web for a cover ≥ 1024×1024…');
    setAutoBusy(true);
    try {
      const found = await searchCoverArt(artist, title, 1024);
      if (!found) {
        setAutoStatus('Nothing matched online — leaving the existing cover untouched.');
        return;
      }
      const blob = new Blob([found.bytes], { type: found.mime });
      // Drop any pending preview URL before minting a new one.
      if (pendingCover?.url) {
        try { URL.revokeObjectURL(pendingCover.url); } catch { /* ignore */ }
      }
      setPendingCover({
        bytes: found.bytes,
        name: found.name,
        mime: found.mime,
        url: URL.createObjectURL(blob),
      });
      setRemoveCover(false);
      try {
        const dim = await getImageDimensions(blob);
        setAutoStatus(`Fetched cover ${dim.width}×${dim.height} from MusicBrainz`);
      } catch {
        setAutoStatus('Fetched cover from MusicBrainz');
      }
    } catch (e: any) {
      setAutoStatus(null);
      setError(e?.message || 'Online cover lookup failed.');
    } finally {
      setAutoBusy(false);
    }
  }, [songName, songAuthor, pendingCover]);

  const onSaveClick = useCallback(async () => {
    setBusy(true);
    setError(null);
    try {
      await onSave({
        songName: songName.trim(),
        songAuthor: songAuthor.trim(),
        cover: pendingCover,
        removeCover,
      });
      // Parent is responsible for calling onClose on success — but in case
      // it returns without closing, we still want to release the URL.
    } catch (e: any) {
      setError(e?.message || 'Save failed');
      setBusy(false);
    }
  }, [onSave, songName, songAuthor, pendingCover, removeCover]);

  return (
    <DialogShell
      title="Edit map info"
      subtitle="Update the song details and cover. Saving writes the chart back to the library — notes and audio stay intact."
      onClose={onClose}
      width={420}
      busy={busy}
      footer={
        <button type="button" style={primaryBtnStyle} onClick={onSaveClick} disabled={busy}>
          {busy ? 'Saving…' : 'Save'}
        </button>
      }
    >
        <label style={labelStyle}>SONG NAME</label>
        <input
          type="text"
          value={songName}
          placeholder="e.g. How You Like That"
          onChange={(e) => setSongName(e.target.value)}
          style={inputStyle}
          disabled={busy}
        />

        <label style={labelStyle}>AUTHOR</label>
        <input
          type="text"
          value={songAuthor}
          placeholder="e.g. BLACKPINK"
          onChange={(e) => setSongAuthor(e.target.value)}
          style={inputStyle}
          disabled={busy}
        />

        {onReplaceAudio && (
          <>
            <label style={labelStyle}>AUDIO</label>
            <div style={{ display: 'flex', gap: 8, alignItems: 'center', marginBottom: 4 }}>
              <span style={{ flex: 1, minWidth: 0, fontSize: 11, color: color.inkDim, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                {audioName || 'No audio loaded'}
              </span>
              <button
                type="button"
                style={secondaryBtnStyle}
                onClick={onReplaceAudio}
                disabled={busy || audioLoading}
                title="Pick an audio file to replace the current track (BPM/offset auto-detected)"
              >
                {audioLoading ? '⏳ …' : <><IcRefresh size={14} /> Change audio</>}
              </button>
            </div>
          </>
        )}

        <button
          type="button"
          style={{
            ...secondaryBtnStyle,
            marginTop: 4,
            opacity: (!songName.trim() && !songAuthor.trim()) ? 0.55 : 1,
          }}
          onClick={autoFetchOnline}
          disabled={busy || autoBusy}
          title={(!songName.trim() && !songAuthor.trim())
            ? 'Type at least a song name or author first'
            : 'Search MusicBrainz for a cover ≥ 1024×1024'}
        >
          <IcSearch size={14} /> Auto-fetch cover from web
        </button>

        {autoStatus && (
          <div style={autoStatusStyle}>
            {autoBusy && <LoaderSpinner />}
            <span>{autoStatus}</span>
          </div>
        )}

        <label style={labelStyle}>COVER</label>
        <input
          ref={coverInputRef}
          type="file"
          accept="image/png,image/jpeg,image/webp,.png,.jpg,.jpeg,.webp"
          onChange={(e) => pickCover(e.target.files?.[0])}
          style={{ display: 'none' }}
        />
        <div style={coverRowStyle}>
          <div
            style={{
              ...coverThumbStyle,
              backgroundImage: previewUrl ? `url(${previewUrl})` : undefined,
              backgroundColor: previewUrl ? 'transparent' : 'rgba(15, 15, 30, 0.5)',
            }}
            onClick={() => !busy && coverInputRef.current?.click()}
          >
            {!previewUrl && <span style={coverPlaceholderStyle}><IcImage size={26} /></span>}
          </div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 4, flex: 1 }}>
            <button
              type="button"
              style={secondaryBtnStyle}
              onClick={() => coverInputRef.current?.click()}
              disabled={busy}
            >
              {previewUrl ? 'Replace cover' : 'Pick cover'}
            </button>
            {previewUrl && (
              <button
                type="button"
                style={cancelBtnStyle}
                onClick={handleRemoveCover}
                disabled={busy}
              >
                Remove
              </button>
            )}
          </div>
        </div>

        {error && <div style={inlineErrorStyle}>{error}</div>}
    </DialogShell>
  );
}

const coverRowStyle: React.CSSProperties = {
  display: 'flex',
  gap: 10,
  alignItems: 'stretch',
};

const coverThumbStyle: React.CSSProperties = {
  width: 86,
  height: 86,
  borderRadius: 8,
  border: '1px solid oklch(0.62 calc(0.11 * var(--tint-c)) var(--tint-h) / 0.35)',
  backgroundSize: 'cover',
  backgroundPosition: 'center',
  cursor: 'pointer',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  flexShrink: 0,
};

const coverPlaceholderStyle: React.CSSProperties = {
  fontSize: 28,
  opacity: 0.6,
};

const autoStatusStyle: React.CSSProperties = {
  fontSize: 10.5,
  color: color.accentSoft,
  background: 'oklch(0.55 calc(0.16 * var(--accent-c)) var(--accent-h) / 0.10)',
  border: '1px solid oklch(0.55 calc(0.16 * var(--accent-c)) var(--accent-h) / 0.25)',
  borderRadius: 6,
  padding: '6px 9px',
  lineHeight: 1.4,
  display: 'flex',
  alignItems: 'center',
  gap: 8,
};

/**
 * Small purple spinner used inside the auto-status pill while an async
 * lookup (ID3 parse, MusicBrainz fetch, image decode) is in flight.
 * Pure CSS — no asset, animation runs via the @keyframes injected once
 * the first time this component mounts.
 */
function LoaderSpinner() {
  useEffect(() => {
    const id = 'flow-dialog-spinner-kf';
    if (document.getElementById(id)) return;
    const style = document.createElement('style');
    style.id = id;
    style.textContent = '@keyframes flow-dlg-spin { to { transform: rotate(360deg); } }';
    document.head.appendChild(style);
  }, []);
  return (
    <span
      style={{
        display: 'inline-block',
        width: 12,
        height: 12,
        borderRadius: '50%',
        border: '1.5px solid rgba(167, 139, 250, 0.25)',
        borderTopColor: color.accentSoft,
        animation: 'flow-dlg-spin 0.7s linear infinite',
        flexShrink: 0,
      }}
    />
  );
}

const inlineErrorStyle: React.CSSProperties = {
  fontSize: 11,
  color: '#FCA5A5',
  background: 'rgba(248, 113, 113, 0.08)',
  border: '1px solid rgba(248, 113, 113, 0.3)',
  borderRadius: 6,
  padding: '8px 10px',
  lineHeight: 1.4,
};

// ── Helpers ─────────────────────────────────────────────────────────────────

function fmtDate(unix: number): string {
  if (!unix) return '—';
  // Backend sends seconds (with sub-second precision). Detect & normalize.
  const ms = unix < 1e11 ? unix * 1000 : unix;
  const d = new Date(ms);
  const now = Date.now();
  const diffH = (now - ms) / 3_600_000;
  if (diffH < 24) {
    return `${d.getHours().toString().padStart(2, '0')}:${d.getMinutes().toString().padStart(2, '0')}`;
  }
  return d.toLocaleDateString();
}

function fmtSize(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / 1024 / 1024).toFixed(2)} MB`;
}

